import { readSheet } from "@bandstand/core/chordPro";
import { transposeKey } from "@bandstand/core/chord";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import styled from "styled-components";

import { count } from "../../count";
import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, IconButton, KeyBadge, Pill } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { PaneHeader, PaneTitle } from "../ui/lists";
import { PhoneBack } from "../ui/PhoneBack";
import { AddSongToSetlistDialog } from "./AddSongToSetlistDialog";

/** A setlist: its songs in order, with the version and key each is played in. */
export const SetlistDetail = observer(function SetlistDetail({ setlistId }: { setlistId: string }) {
    const { library, navigation } = useStores();
    const [adding, setAdding] = useState(false);
    const [keys, setKeys] = useState<Record<string, string | undefined>>({});
    const setlist = library.setlist(setlistId);

    // The key of each song comes from its file, so read them once the setlist changes.
    useEffect(() => {
        let current = true;

        void Promise.all(
            (setlist?.items ?? []).map(async (item) => [item.chordSheetId, readSheet(await library.text(item.chordSheetId)).key] as const),
        ).then((entries) => current && setKeys(Object.fromEntries(entries)));

        return () => {
            current = false;
        };
    }, [library, setlist]);

    if (!setlist) return null;

    const play = (index: number) =>
        navigation.go({ name: "sheet", chordSheetId: setlist.items[index].chordSheetId, from: { kind: "setlist", setlistId, index } });

    const last = setlist.items.length - 1;
    const move = (index: number, by: number) => library.change((l) => l.moveInSetlist(setlistId, index, index + by));
    const removeSong = (index: number) => library.change((l) => l.removeFromSetlist(setlistId, index));
    const date = setlist.date && new Date(setlist.date).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

    async function deleteSetlist() {
        if (!confirm(`Delete the setlist "${setlist!.name}"? Its songs stay.`)) return;

        await library.change((l) => l.deleteSetlist(setlistId));
        navigation.replace({ name: "setlists" });
    }

    return (
        <>
            <PaneHeader>
                <PhoneBack label="Back to setlists" onClick={() => navigation.go({ name: "setlists" })} />
                <PaneTitle>{setlist.name}</PaneTitle>
            </PaneHeader>
            <Summary>
                {date && `${date} · `}
                {count(setlist.items.length, "song")}
            </Summary>
            <Actions>
                <Button $kind="primary" disabled={setlist.items.length === 0} onClick={() => play(0)}>
                    <Icon name="play" size={16} />
                    Play from the start
                </Button>
                <Button onClick={() => setAdding(true)}>Add a song</Button>
                <Button $kind="danger" onClick={deleteSetlist}>
                    Delete setlist
                </Button>
            </Actions>
            <Items>
                {setlist.items.map((item, index) => {
                    const sheet = library.chordSheet(item.chordSheetId);
                    const title = library.song(sheet?.songId)?.title;
                    const key = keys[item.chordSheetId];
                    const transpose = item.transpose === 0 ? "" : `${item.transpose > 0 ? "+" : ""}${item.transpose}`;

                    return (
                        <Item key={`${item.chordSheetId}-${index}`}>
                            <Number>{index + 1}</Number>
                            <Open onClick={() => play(index)}>
                                <ItemTitle>{title}</ItemTitle>
                                <ItemDetail>
                                    {sheet?.name && <span>{sheet.name}</span>}
                                    {sheet?.setlistId && <Pill $tone="setlistOnly">this setlist only</Pill>}
                                    {transpose && <span>transposed {transpose}</span>}
                                </ItemDetail>
                            </Open>
                            {key && <KeyBadge>{transposeKey(key, item.transpose)}</KeyBadge>}
                            <Moves>
                                <IconButton aria-label={`Move ${title} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                                    <Icon name="up" />
                                </IconButton>
                                <IconButton aria-label={`Move ${title} down`} disabled={index === last} onClick={() => move(index, 1)}>
                                    <Icon name="down" />
                                </IconButton>
                                <IconButton aria-label={`Remove ${title}`} onClick={() => removeSong(index)}>
                                    <Icon name="close" size={20} />
                                </IconButton>
                            </Moves>
                        </Item>
                    );
                })}
            </Items>
            {adding && <AddSongToSetlistDialog setlistId={setlistId} onClose={() => setAdding(false)} />}
        </>
    );
});

const Summary = styled.p`
    margin: 0;
    padding: 0 16px 16px;
    font-size: 15px;
    color: ${color.textMuted};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 16px 16px;
`;

const Items = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0 16px;
`;

const Item = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 72px;
    border-bottom: 1px solid ${color.border};
`;

const Number = styled.span`
    width: 22px;
    font-size: 16px;
    font-weight: 600;
    color: ${color.textMuted};
`;

const Open = styled.button`
    flex: 1;
    min-width: 0;
    padding: 12px 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ItemTitle = styled.span`
    font-size: 17px;
    font-weight: 600;
`;

const ItemDetail = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${color.textMuted};
`;

const Moves = styled.span`
    display: flex;
`;
